// React core and hooks
import React, { useState, useCallback, useMemo } from "react";

// Styling and themes
import "../App.css";

// Material UI components and icons
import { Grid, Stack, Tooltip, IconButton } from "@mui/material";
import FastForwardIcon from "@mui/icons-material/FastForward";
import FastRewindIcon from "@mui/icons-material/FastRewind";
import StopIcon from "@mui/icons-material/Stop";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import VolumeDown from "@mui/icons-material/VolumeDown";
import VolumeUp from '@mui/icons-material/VolumeUp';
import VolumeOff from '@mui/icons-material/VolumeOff';
import SubtitlesOutlinedIcon from '@mui/icons-material/SubtitlesOutlined';
import SubtitlesOffOutlinedIcon from '@mui/icons-material/SubtitlesOffOutlined';

// Internationalization
import { useTranslation } from "react-i18next";
import i18n from '../i18n.js';

// Custom components
import PropTypes from 'prop-types';
import VolumeSlider from './VolumeSlider';
import { FullScreen } from "./FullScreen.js";
import PlayerCommands2 from "./PlayerCommands2";
import TabButtons from "./TabButtons.js";

// Context providers
import { useEnvironments } from "../contexts/EnvironmentsContext.js";

// Other utilities and components
import { useResponsive } from '../utils/useResponsive';
import Playlists from "../Playlists/playlists/PlayLists";
import { deletePlaylist } from "../utils/api";
import { handlePlayMontageEnd } from "../Play/playModeUtils.js";

export default function PlayerCommands ({
  onPlay, onPause, onStop, onRew, onFwd, isPlaying, setIsPlaying, iconClass, t, volumeRef, onVolumeChange, onPlayStart, onPlayEnd, playModeRef, 
  currentPlaylist, currentTempPlaylistId, house, setPlaylists, responsiveProps
 }) {

  console.log('[PlayerCommands] Rendering with volumeRef.current, currentPlaylist):', volumeRef.current, currentPlaylist );
  const { handlePlaylistChange } = useEnvironments(); // Only get what you need

  const { isMobile } = responsiveProps;
  console.log('[PlayerCommands] isPlaying', isPlaying);
  // We'll trace both volume display value and actual audio volume
  const [muted, setMuted] = useState(false);

  const iconStyle = {
  color: '#333333', // Your desired icon color
  fill: '#333333',
  opacity: 1,
  visibility: 'visible'
  };

  // Volume change handler (used by both controls)
  const handleVolumeChange = useCallback((newVolume) => {
    console.log('[PlayerCommands] handleVolumeChange called with:', newVolume);

    // Always update the display volume
    volumeRef.current = newVolume;
    // Update ref and localStorage atomically
    localStorage.setItem('wallmuse-volume', newVolume.toString());

    onVolumeChange(newVolume);

    // Automatically unmute if user changes volume to non-zero
    if (newVolume > 0 && muted) {
      setMuted(false);
    } else if (newVolume === 0 && !muted) {
      setMuted(true);
    }
  }, [onVolumeChange, muted]);

  // Toggle mute handler with improved behavior
  const toggleMute = useCallback(() => {
    // Toggle muted state
    const newMutedState = !muted;
    setMuted(newMutedState);
    
    // When unmuting, use the display volume
    if (!newMutedState) {
      // Unmuting - set actual volume to display volume
      console.log('[VolumeControl] Unmuting. Setting volume to:', 50);
      volumeRef.current = 50;
      onVolumeChange(50); // Add this line to actually change volume
    } else {
      console.log('[VolumeControl] Muting. Setting volume to:', 0);
      volumeRef.current = 0;
      onVolumeChange(0); // Add this line to actually change volume
    }

  }, [muted, onVolumeChange]);

  // Real toggle approach for play/pause (not optimistic)
 
  const handlePlayPause = useCallback(() => {
    console.log('[PlayerCommands handlePlayPause] isPlaying', isPlaying)
    if (isPlaying) {
      setIsPlaying(false); // Optimistically update
      onPause();
    } else {
      setIsPlaying(true); // Optimistically update
      onPlay();
    }
  }, [isPlaying, onPlay, onPause, setIsPlaying]);

  const handleStopWithIconUpdate = useCallback(() => {
    onStop();
    // Update playing state to show play icon
    setIsPlaying(false);
  }, [onStop]);

    // Stop use case in Play Mode
    const handleStopPlayMode = async () => {
      try {
        // Then do cleanup if we were in Play Mode
        if (playModeRef.current && currentTempPlaylistId) {
          await handlePlayMontageEnd(currentTempPlaylistId, {
            house,
            handlePlaylistChange, //playlist change 
            currentPlaylist,
            setPlaylists,
            playModeRef,
            // Don't need to stop again since we already did it
            onPlayEnd,
        }); 
      } else {
          console.log("[PlayerCommands] Not in Play Mode or no temp playlist, stop only");
          playModeRef.current = false;
      }
      } catch (error) {
        console.error("[PlayerCommands] Error in stop handling:", error);
        playModeRef.current = false;
      }
    };

  return useMemo(() => (
    <Grid container spacing={1} className="player_commands" sx={{ flexGrow: 1 }}>
      {/* Volume control - reduced to xs={1} on mobile */}
      <Grid item xs={isMobile ? 2 : 3} style={{ textAlign: 'left', marginLeft: '0' }}>
        {isMobile ? (
          <Tooltip title={muted ? t("Unmute") : t("Mute")}>
            {/* TODO change to unmute_ and mute_ with translations */}
            <IconButton 
              onClick={toggleMute} 
              size="small"
              sx={{
                position: 'relative',
                zIndex: 5,
                visibility: 'visible', // Force visibility
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.08)', // Add hover effect
                }
              }}
              style={iconStyle}
            >
              {muted ? (
                <VolumeOff
                  className={`tabs_icon ${iconClass}`}
                  // style={{ 
                  //   color: 'inherit',
                  //   visibility: 'visible', // Force icon visibility
                  //   opacity: 1 // Ensure full opacity
                  // }}
                  style={iconStyle}
                />
              ) : (
                <VolumeUp
                  className={`tabs_icon ${iconClass}`}
                  // style={{ 
                  //   color: 'inherit',
                  //   visibility: 'visible', // Force icon visibility
                  //   opacity: 1 // Ensure full opacity
                  // }}
                  style={iconStyle}
                />
              )}
            </IconButton>
          </Tooltip>
        ) : (
          <Stack spacing={1} direction="row" sx={{ mt: 0.5, mb: 0.5 }} >
              <VolumeDown
                style={{ color: 'inherit', pointerEvents: "none", marginLeft: '0' }}
                fontSize="small"
                className={`tabs_icon ${iconClass}`}
                style={iconStyle}
              />
              <VolumeSlider volumeRef={volumeRef} onVolumeChange={handleVolumeChange} style={iconStyle}/>
    
          </Stack>
        )}
      </Grid>

      {/* Playback controls - take more space on mobile */}
      <Grid item xs={isMobile ? 8 : 6} style={{ display: 'flex', textAlign: 'centre', justifyContent: 'centre', flexBasis: 'auto' }}>
        <Tooltip title={t("backward_")}>
          <IconButton onClick={onRew} disabled={playModeRef.current}>
            <FastRewindIcon
              color="secondary.text"
              className={`tabs_icon ${iconClass}`}
              style={iconStyle}
            />
          </IconButton>
        </Tooltip>
        <Tooltip title={ isPlaying? t("pause_") : t("play_")}>
          <IconButton onClick={handlePlayPause} disabled={playModeRef.current}>
            {isPlaying ? (
                <PauseIcon color="secondary.text" className={`tabs_icon ${iconClass}`} style={iconStyle} />
            ) : (
              <PlayArrowIcon color="secondary.text" className={`tabs_icon ${iconClass}`} style={iconStyle} />
            )}
          </IconButton>
        </Tooltip>
        <Tooltip title={t("stop_")}>
          <IconButton onClick={playModeRef.current ? handleStopPlayMode : handleStopWithIconUpdate} >
            <StopIcon color="secondary.text" className={`tabs_icon ${iconClass}`} style={iconStyle}/>
          </IconButton>
        </Tooltip>
        <Tooltip title={t("forward_")}>
          <IconButton onClick={onFwd} disabled={playModeRef.current}>
            <FastForwardIcon 
              color="secondary.text"
              className={`tabs_icon ${iconClass}`}
              style={iconStyle}
            />
          </IconButton>
        </Tooltip>
      </Grid>
      {/* TODO place on Line 2 */}
      {/* <Grid item xs={1} alignItems="right"> */}
      {/* Subtitles control if needed */}
      {/* <Tooltip title={t("subtitles_")}>
        <IconButton onClick={() => setSubtitled(prevSubtitled => !prevSubtitled)}>
          {setSubtitled ? (
            <SubtitlesOutlinedIcon color="secondary.text" className={`tabs_icon ${iconClass}`} />
          ) : (
            <SubtitlesOffOutlinedIcon color="secondary.text" className={`tabs_icon ${iconClass}`} />
          )}
        </IconButton>
      </Tooltip> */}
      {/* </Grid> */}

      <Grid item xs={isMobile ? 2 : 3} style={{ display: 'flex', justifyContent: 'right' }}>
        <FullScreen
          color="secondary.text"
          className={`tabs_icon ${iconClass}`}
          style={iconStyle}
        />
      </Grid>
    </Grid>
  ), [
    // Dependencies that should trigger re-renders when they change
    isPlaying,
    muted,
    playModeRef.current,
    currentPlaylist, 
    currentTempPlaylistId,
    handlePlayPause,
    handleStopWithIconUpdate,
    handleStopPlayMode,
    onRew,
    onFwd,
    iconClass,
    isMobile,
    toggleMute,
    handleVolumeChange
  ]);
};

PlayerCommands.propTypes = {
    onPlay: PropTypes.func,
    onPause: PropTypes.func,
    onStop: PropTypes.func,
    onRew: PropTypes.func,
    onFwd: PropTypes.func,
    isPlaying: PropTypes.bool,
    setIsPlaying:  PropTypes.func,
    setSubtitled: PropTypes.func,
    iconClass: PropTypes.string,
    responsiveProps: PropTypes.shape({
      isMobile: PropTypes.bool,
      isTablet: PropTypes.bool,
      isHD: PropTypes.bool,
      isUHD: PropTypes.bool,
      isSmartTV: PropTypes.bool,
      isSmartTVHD: PropTypes.bool,
      isSmartTVUHD: PropTypes.bool,
      isPortrait: PropTypes.bool,
      isLandscape: PropTypes.bool
    }),
    t: PropTypes.func,
    volumeRef: PropTypes.object,
    onVolumeChange: PropTypes.func,
    playModeRef: PropTypes.object,
    currentPlaylist: PropTypes.string,
    currentTempPlaylistId: PropTypes.string,
    house: PropTypes.string,
    setPlaylists: PropTypes.func,
    handlePlaylistChange: PropTypes.func,
    deletePlaylist: PropTypes.func,
    handlePlayMontageEnd: PropTypes.func
  };